import React from "react";
import _ from "lodash";
import JumpTabList from "./JumpTabList";
import SimpleTooltip from "./SimpleTooltip";
import { FiInfo } from "react-icons/fi";
import ConfigValueControl from "components/control-center/auto-pilot/config/ConfigValueControl";
import { MESSAGE } from "constants/Message";
import { GROUP_CATEGORY } from "components/ops-mgmt/account/network-features/FeaturesSchema";

class SchemaConfigTable extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      currentEditingKey: "",

      groupedSchemas: {},
    };

    this.handleStartEditMode = this.handleStartEditMode.bind(this);
    this.handleCancelEditMode = this.handleCancelEditMode.bind(this);
    this.handleApplyChanges = this.handleApplyChanges.bind(this);

    this.getKeyList = this.getKeyList.bind(this);
    this.getGroupedSchemas = this.getGroupedSchemas.bind(this);
  }

  componentDidMount() {
    const { configSchemas, newConfig } = this.props;
    if (!configSchemas) return;

    this.setState({
      groupedSchemas: this.getGroupedSchemas(configSchemas, newConfig),
    });
  }

  componentDidUpdate(prevProps) {
    const { configSchemas, newConfig } = this.props;
    if (!configSchemas) return;

    if (
      prevProps.configSchemas !== configSchemas ||
      prevProps.newConfig !== newConfig
    ) {
      this.setState({
        groupedSchemas: this.getGroupedSchemas(configSchemas, newConfig),
      });
    }
  }

  handleStartEditMode(key) {
    if (this.props.handleStartEditMode) {
      this.props.handleStartEditMode();
    }
    this.setState({
      currentEditingKey: key,
    });
  }

  handleCancelEditMode() {
    if (this.props.handleCancelEditMode) {
      this.props.handleCancelEditMode();
    }
    this.setState({
      currentEditingKey: "",
    });
  }

  handleApplyChanges(params) {
    this.setState({
      currentEditingKey: "",
    });

    if (this.props.handleApplyChanges) {
      this.props.handleApplyChanges(params);
    }
  }

  getKeyList(schema) {
    const { section, prefix, key } = schema;
    return [
      ...(section || "").split("."),
      ...(prefix || "").split("."),
      key,
    ].filter((key) => key !== "");
  }

  getGroupedSchemas(schemas, config) {
    const schemaMap = _.keyBy(schemas, "key");
    const groupedCategorySchemas = _.groupBy(
      schemas.filter((d) => d.groupCategory !== GROUP_CATEGORY.HIDE),
      (s) => {
        return s.groupCategory;
      },
    );

    const parsedGroupedSchemas = _.mapValues(groupedCategorySchemas, (list) => {
      return _.map(list, (s) => {
        const configValue = _.get(config, this.getKeyList(s));
        const configKeyTitle = _.startCase(s.key);

        const relatedSchema = _.map(s.relatedKey, (key) => {
          return schemaMap[key];
        }).filter((s) => !_.isNil(s));

        const relatedValue = _.map(relatedSchema, (schema) => {
          return _.get(config, this.getKeyList(schema));
        });

        return {
          schema: s,
          configValue,
          configKeyTitle,
          relatedSchema,
          relatedValue,
        };
      });
    });

    return parsedGroupedSchemas;
  }

  render() {
    const { currentEditingKey, groupedSchemas } = this.state;

    const { configSchemas, showJumper = true, networkId, unitId } = this.props;

    if (!configSchemas) {
      return "";
    }

    const isGrouping = _.every(configSchemas, (s) => {
      return s.groupCategory;
    });

    return (
      <>
        {isGrouping && showJumper && (
          <div className="pl-8">
            <JumpTabList tabs={_.keys(groupedSchemas)}></JumpTabList>
          </div>
        )}

        {_.keys(groupedSchemas).map((category) => {
          const schemaList = groupedSchemas[category];

          return (
            <div key={category} id={category} className="mb-12">
              {category !== "undefined" && (
                <div className="border border-blue-200 bg-blue-100 py-4 pl-8 text-xl font-semibold text-blue-800">
                  {`${category} Settings`}
                </div>
              )}

              <div className="border bg-white">
                {schemaList.map(
                  ({
                    schema,
                    configValue,
                    configKeyTitle,
                    relatedSchema,
                    relatedValue,
                  }) => {
                    const s = schema;

                    return (
                      <div
                        key={s.key}
                        className="border-b flex border-gray-400"
                      >
                        <div className="w-1/3 py-6 pl-8">
                          <div
                            className={`break-words pr-8 text-sm font-semibold ${
                              s.isDeprecated ? "text-orange-700" : ""
                            }`}
                          >
                            {s.title ? s.title : configKeyTitle}:
                          </div>
                          {s.note && (
                            <div className="text-xs text-gray-600">
                              {s.note}
                            </div>
                          )}
                          {s.description && (
                            <SimpleTooltip
                              place="right"
                              tooltipText={
                                <div
                                  className="block whitespace-pre-line break-words text-left"
                                  style={{ width: "300px" }}
                                >
                                  <div className="text-left">
                                    <b>{configKeyTitle}:</b>
                                  </div>
                                  {s.description}
                                </div>
                              }
                            >
                              <div className="mt-2 text-xs text-blue-600">
                                <FiInfo></FiInfo>
                              </div>
                            </SimpleTooltip>
                          )}
                        </div>

                        <div
                          className={`w-2/3 bg-gray-200 py-6 ${
                            currentEditingKey === s.key
                              ? "border-2 border-blue-600"
                              : ""
                          }`}
                        >
                          <ConfigValueControl
                            unitId={unitId}
                            networkId={networkId}
                            configValue={configValue}
                            valueSchema={s}
                            relatedValue={relatedValue}
                            relatedSchema={relatedSchema}
                            currentEditingKey={currentEditingKey}
                            handleStartEditMode={this.handleStartEditMode}
                            handleCancelEditMode={this.handleCancelEditMode}
                            handleApplyChanges={this.handleApplyChanges}
                          ></ConfigValueControl>

                          {currentEditingKey !== s.key &&
                            s.msgFunc &&
                            s.msgFunc(configValue, s.key).length > 0 && (
                              <div
                                className={`rounded ml-6 mr-4 mt-2 px-2 py-1 font-semibold ${
                                  s.msgFunc(configValue, s.key)[1] ===
                                  MESSAGE.WARNING
                                    ? "bg-orange-200 text-orange-600"
                                    : "bg-blue-200 text-blue-600"
                                }`}
                              >
                                {s.msgFunc(configValue, s.key)[0]}
                              </div>
                            )}
                        </div>
                      </div>
                    );
                  },
                )}
              </div>
            </div>
          );
        })}
      </>
    );
  }
}

export default SchemaConfigTable;
